<template>
    <div>
        <user-layout>
            <div v-for="notification in notifications"
                 :key="notification.content">
              <Message :severity="notification.severity" @close="closeNotification(notification.clearUrl)">
                <template>
                  <span v-html="notification.content"></span>
                </template>
              </Message>
            </div>

            <h1 class="mb-6 text-2xl font-bold leading-tight text-black md:text-4xl">Minu projektid</h1>
                    <router-link v-if="UserRole.contains(UserRole.CUSTOMER, user.active_roles)" to="/project/new" class="text-orange-400 bg-orange-200 btn-sm rounded-full">
                        <span class="mr-3 text-xs font-bold pi pi-plus"></span>
                        Uus projekt
                    </router-link>

                    <tab-view class="mt-6">
                        <tab-panel>
                            <template slot="header">
                                <h3 class="text-lg">Aktiivsed</h3>
                            </template>

                            <div class="py-8">
                                <div class="flex flex-wrap -mx-3">
                                    <div class="w-full px-3 md:w-1/2">
                                        <router-link
                                            v-for="project in projects.active"
                                            :key="'active_' + project.id + '_' + project.projectUser.id"
                                            :to="'/project/' + project.projectUser.id"
                                            class="flex items-center justify-between px-4 py-4 mb-4 bg-white rounded-lg shadow-lg md:px-6"
                                        >
                                            <div>
                                                <h4 class="mb-1 text-lg leading-tight text-black">
                                                    <strong>{{ project && project.name }}</strong> {{ project && project.size }}m² <span class="text-sm text-gray-400 uppercase">{{project.projectUser.areaShort}}</span>
                                                </h4>
                                                <p class="text-sm text-gray-600">{{ project && project.status && project.status.subtitle }}</p>
                                            </div>

                                            <span class="ml-3 text-xl text-blue-500 pi pi-chevron-circle-right"></span>
                                        </router-link>

                                        <h4
                                            v-if="projects.active.length > 0 && projects.pending.length > 0"
                                            class="py-3 px-6 text-sm text-blue-500 uppercase"
                                        >Ootel projektid</h4>
                                        <router-link
                                            v-for="project in projects.pending"
                                            :key="'pending_' + project.id + '_' + project.projectUser.id"
                                            :to="`/project/${project.projectUser.id}/${(project.can_pick_teamlead && project.projectUser.role.id === ProjectUserRole.CLIENT) ? 'pick-manager' : ''}`"
                                            class="flex items-center justify-between px-4 py-4 mb-4 bg-white rounded-lg shadow-lg md:px-6"
                                        >
                                            <div>
                                                <h4 class="mb-1 text-lg leading-tight text-black">
                                                    <strong>{{ project.name }}</strong> {{ project.size }}m² <span class="text-sm text-gray-400 uppercase">{{project.projectUser.areaShort}}</span>
                                                </h4>
                                                <p v-if="project.projectUser.role.id === ProjectUserRole.CLIENT" class="text-sm text-gray-600">
                                                    <template v-if="project.status.id === 8">
                                                        Meeskond on moodustamisel
                                                    </template>
                                                    <template v-else>
                                                        {{ project.status.subtitle }}
                                                    </template>
                                                </p>
                                                <p v-else class="text-sm text-gray-600">
                                                    <template v-if="project.can_lead && project.status.id === 8">
                                                        {{ project.status.subtitle }}
                                                    </template>
                                                    <template
                                                        v-else-if="!project.can_lead && project.can_pick_tasks && !project.tasks_picked">
                                                        Kinnita projekti ülesanded
                                                    </template>
                                                    <template v-else>
                                                        Ootel
                                                    </template>
                                                </p>
                                            </div>

                                            <span class="ml-3 text-xl text-yellow-600 pi pi-clock"></span>
                                        </router-link>
                                    </div>

                                    <div class="w-full md:w-1/2">
                                        <!-- Tellija -->
                                        <template v-if="projects.active.length > 0
                                                        && (projects.active.some(p => ProjectUserRole.CLIENT === p.projectUser.role.id)
                                                        || projects.pending.some(p => ProjectUserRole.CLIENT === p.projectUser.role.id))">
                                            <h4 class="py-3 px-6 text-sm text-blue-500 uppercase">Tellija</h4>
                                            <div class="flex flex-wrap">
                                                <div class="w-full px-3 mb-6 sm:w-1/2 md:w-full lg:w-1/2">
                                                    <div class="p-4 bg-white rounded-lg md:p-6 h-full">
                                                        <div
                                                            class="inline-flex items-center justify-center w-16 h-16 mb-3 text-blue-500 bg-blue-100 rounded-xl"
                                                        >
                                                            <svg class="w-6 h-6" viewBox="0 0 24 24"
                                                                 xmlns="http://www.w3.org/2000/svg">
                                                                <g
                                                                    fill="none"
                                                                    fill-rule="evenodd"
                                                                    stroke="currentColor"
                                                                    stroke-linecap="square"
                                                                    stroke-width="2"
                                                                    transform="translate(1 1)"
                                                                >
                                                                    <path d="m11 22v-10" />
                                                                    <path d="m11 22c0-3.314 2.686-6 6-6h2c0 3.314-2.686 6-6 6z" />
                                                                    <path d="m0 0h22v12h-22z" />
                                                                    <circle cx="11" cy="6" r="2" />
                                                                </g>
                                                            </svg>
                                                        </div>
                                                        <h4 class="mb-2 text-4xl font-bold leading-none text-black">{{ clientSummary.contractsSum }}€</h4>
                                                        <p class="text-sm text-gray-600 md:text-base">Lepingute maksumus</p>
                                                    </div>
                                                </div>

                                                <div class="w-full px-3 mb-6 sm:w-1/2 md:w-full lg:w-1/2">
                                                    <div class="p-4 bg-white rounded-lg md:p-6 h-full">
                                                        <div
                                                            class="inline-flex items-center justify-center w-16 h-16 mb-3 text-yellow-500 bg-yellow-100 rounded-xl"
                                                        >
                                                            <svg class="w-6 h-6" viewBox="0 0 22 22"
                                                                 xmlns="http://www.w3.org/2000/svg">
                                                                <g fill="none" fill-rule="evenodd">
                                                                    <circle cx="10.8" cy="10.8" r="9.84" stroke="currentColor"
                                                                            stroke-width="1.92" />
                                                                    <path
                                                                        d="m11.0068574 11.1921569h-5.42057223v-6.58823533c0-.55441569-.44467959-1.00392157-.99314258-1.00392157-.548463 0-.99314259.44950588-.99314259 1.00392157v7.59215683c0 .5544157.44467959 1.0039216.99314259 1.0039216h6.41371481c.548463 0 .9931426-.4495059.9931426-1.0039216s-.4446796-1.0039215-.9931426-1.0039215z"
                                                                        fill="currentColor"
                                                                        fill-rule="nonzero"
                                                                        transform="matrix(-1 0 0 1 15.6 0)"
                                                                    />
                                                                </g>
                                                            </svg>
                                                        </div>
                                                        <h4 class="mb-2 text-4xl font-bold leading-none text-black">{{ clientSummary.pendingSum || 0 }}€</h4>
                                                        <p class="text-sm text-gray-600 md:text-base">Ootel projektide summa</p>
                                                    </div>
                                                </div>

                                                <div class="w-full px-3 mb-6 sm:w-1/2 md:w-full lg:w-1/2">
                                                    <div class="p-4 bg-white rounded-lg md:p-6 h-full">
                                                        <div
                                                            class="inline-flex items-center justify-center w-16 h-16 mb-3 text-red-600 bg-red-100 rounded-xl"
                                                        >
                                                            <svg
                                                                class="w-6 h-6"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    stroke-linecap="round"
                                                                    stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                                                                />
                                                            </svg>
                                                        </div>
                                                        <h4 class="mb-2 text-4xl font-bold leading-none text-black">{{ clientSummary.notDepositedSum }}€</h4>
                                                        <p class="text-sm text-gray-600 md:text-base">Deponeerimata summa</p>
                                                    </div>
                                                </div>

                                                <div class="w-full px-3 mb-6 sm:w-1/2 md:w-full lg:w-1/2">
                                                    <div class="p-4 bg-white rounded-lg md:p-6 h-full">
                                                        <div
                                                            class="inline-flex items-center justify-center w-16 h-16 mb-3 text-blue-500 bg-blue-100 rounded-xl"
                                                        >
                                                            <svg class="w-6 h-6" viewBox="0 0 22 22"
                                                                 xmlns="http://www.w3.org/2000/svg">
                                                                <g fill="none" fill-rule="evenodd" stroke="currentColor">
                                                                    <circle cx="10.8" cy="10.8" r="9.84" stroke-width="2" />
                                                                    <path
                                                                        d="m7 10.7146581 3.6959716 2.714658 3.8186761-6.4293161"
                                                                        stroke-linecap="round"
                                                                        stroke-width="2"
                                                                    />
                                                                </g>
                                                            </svg>
                                                        </div>
                                                        <h4 class="mb-2 text-4xl font-bold leading-none text-black">{{clientSummary.depositedSum}}€</h4>
                                                        <p class="text-sm text-gray-600 md:text-base">Deponeeritud summa</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </template>

                                        <!-- Projektijuht / Projekteeria -->
                                        <template v-if="projects.active.length > 0
                                                        && (projects.active.some(p => ProjectUserRole.isSpecialistExtended(p.projectUser.role.id))
                                                        || projects.pending.some(p => ProjectUserRole.isSpecialistExtended(p.projectUser.role.id)))">
                                            <h4 class="py-3 px-6 text-sm text-blue-500 uppercase">Spetsialist</h4>
                                            <div class="w-full px-3 mb-6">
                                                <div class="p-4 bg-white rounded-lg md:p-6 flex items-center">
                                                    <div
                                                        class="inline-flex items-center justify-center w-16 h-16 mr-3 md:mr-6 text-blue-500 bg-blue-100 rounded-xl"
                                                    >
                                                        <svg class="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                            <g
                                                                fill="none"
                                                                fill-rule="evenodd"
                                                                stroke="currentColor"
                                                                stroke-linecap="square"
                                                                stroke-width="2"
                                                                transform="translate(1 1)"
                                                            >
                                                                <path d="m11 22v-10" />
                                                                <path d="m11 22c0-3.314 2.686-6 6-6h2c0 3.314-2.686 6-6 6z" />
                                                                <path d="m0 0h22v12h-22z" />
                                                                <circle cx="11" cy="6" r="2" />
                                                            </g>
                                                        </svg>
                                                    </div>

                                                    <div class="flex-1">
                                                        <h4 class="mb-2 text-2xl md:text-4xl font-bold leading-none text-black">
                                                            {{ specialistSummary.active_sum }}€</h4>
                                                        <p class="text-sm text-gray-600 md:text-base">Aktiivsete projektide
                                                            maksumus</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="flex flex-wrap">
                                                <div class="flex flex-col w-full px-3 sm:w-1/2 md:w-full lg:w-1/2">
                                                    <div
                                                        class="py-4 bg-white rounded-lg px-4 md:py-6 mb-6 flex flex-wrap items-center flex-grow">
                                                        <div
                                                            class="inline-flex items-center justify-center w-16 h-16 mr-3 text-red-600 bg-red-100 rounded-xl"
                                                        >
                                                            <svg
                                                                class="w-6 h-6"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    stroke-linecap="round"
                                                                    stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                                                                />
                                                            </svg>
                                                        </div>

                                                        <div class="flex-1">
                                                            <h4 class="mb-2 text-xl md:text-3xl font-bold leading-none text-black">
                                                                {{ specialistSummary.tasks.active.todo_sum }}€</h4>
                                                            <p class="text-sm text-gray-600 md:text-base">Tegemata töid</p>
                                                        </div>
                                                    </div>

                                                    <div
                                                        class="py-4 bg-white rounded-lg px-4 md:py-6 mb-6 flex flex-wrap items-center flex-grow">
                                                        <div
                                                            class="inline-flex items-center justify-center w-16 h-16 mr-3 text-blue-500 bg-blue-100 rounded-xl"
                                                        >
                                                            <svg class="w-6 h-6" viewBox="0 0 22 22"
                                                                 xmlns="http://www.w3.org/2000/svg">
                                                                <g fill="none" fill-rule="evenodd" stroke="currentColor">
                                                                    <circle cx="10.8" cy="10.8" r="9.84" stroke-width="2" />
                                                                    <path
                                                                        d="m7 10.7146581 3.6959716 2.714658 3.8186761-6.4293161"
                                                                        stroke-linecap="round"
                                                                        stroke-width="2"
                                                                    />
                                                                </g>
                                                            </svg>
                                                        </div>

                                                        <div class="flex-1">
                                                            <h4 class="mb-2 text-xl md:text-3xl font-bold leading-none text-black">
                                                                {{ specialistSummary.tasks.active.done_sum }}€</h4>
                                                            <p class="text-sm text-gray-600 md:text-base">Tehtud töid</p>
                                                        </div>
                                                    </div>

                                                    <div
                                                        class="py-4 bg-white rounded-lg px-4 md:py-6 flex flex-wrap items-center flex-grow">
                                                        <div
                                                            class="inline-flex items-center justify-center w-16 h-16 mr-3 text-yellow-500 bg-yellow-100 rounded-xl"
                                                        >
                                                            <svg class="w-6 h-6" viewBox="0 0 22 22"
                                                                 xmlns="http://www.w3.org/2000/svg">
                                                                <g fill="none" fill-rule="evenodd">
                                                                    <circle cx="10.8" cy="10.8" r="9.84" stroke="currentColor"
                                                                            stroke-width="1.92" />
                                                                    <path
                                                                        d="m11.0068574 11.1921569h-5.42057223v-6.58823533c0-.55441569-.44467959-1.00392157-.99314258-1.00392157-.548463 0-.99314259.44950588-.99314259 1.00392157v7.59215683c0 .5544157.44467959 1.0039216.99314259 1.0039216h6.41371481c.548463 0 .9931426-.4495059.9931426-1.0039216s-.4446796-1.0039215-.9931426-1.0039215z"
                                                                        fill="currentColor"
                                                                        fill-rule="nonzero"
                                                                        transform="matrix(-1 0 0 1 15.6 0)"
                                                                    />
                                                                </g>
                                                            </svg>
                                                        </div>

                                                        <div class="flex-1">
                                                            <h4 class="mb-2 text-xl md:text-3xl font-bold leading-none text-black">
                                                                {{ specialistSummary.pending_sum }}€</h4>
                                                            <p class="text-sm text-gray-600 md:text-base">Ootel projektid</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div class="w-full px-3 sm:w-1/2 md:w-full lg:w-1/2 mt-6 sm:mt-0  md:mt-6 lg:mt-0">
                                                    <div class="p-4 bg-white rounded-lg md:p-6 h-full">
                                                        <div class="relative">
                                                            <chart type="doughnut" :data="chartData" :options="chartOptions" />

                                                            <div class="absolute inset-0 flex items-center justify-center">
                                                                <div class="text-center w-1/2">
                                                                    <h4 class="mb-2 text-xl md:text-3xl font-bold leading-none text-black">
                                                                        {{ specialistSummary.tasks.active.total }}</h4>
                                                                    <p class="text-gray-600 text-sm">Ülesannet kokku</p>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div class="flex my-4">
                                                            <div class="w-3 h-3 bg-blue-500 rounded-full mr-6 my-2"></div>
                                                            <div class="flex-1">
                                                                <h4 class="mb-2 text-xl md:text-3xl font-bold leading-none text-black">
                                                                    {{ specialistSummary.tasks.active.done }}</h4>
                                                                <p class="text-gray-600 text-sm">Tehtud ülesandeid kokku</p>
                                                            </div>
                                                        </div>

                                                        <div class="flex my-4">
                                                            <div class="w-3 h-3 bg-orange-400 rounded-full mr-6 my-2"></div>
                                                            <div class="flex-1">
                                                                <h4 class="mb-2 text-xl md:text-3xl font-bold leading-none text-black">
                                                                    {{ specialistSummary.tasks.active.todo }}</h4>
                                                                <p class="text-gray-600 text-sm">Tegemata ülesandeid kokku</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </tab-panel>

                        <tab-panel>
                            <template slot="header">
                                <h3 class="text-lg">Lõpetatud</h3>
                            </template>

                            <div class="py-8 -mx-3">
                                <div class="px-3 w-full md:w-1/2">
                                    <router-link
                                        v-for="project in projects.ended"
                                        :key="'stopped_' + project.name"
                                        :to="'/project/' + project.projectUser.id"
                                        class="flex items-center justify-between px-4 py-4 mb-4 bg-white rounded-lg shadow-lg md:px-6"
                                    >
                                        <div>
                                            <h4 class="mb-1 text-lg leading-tight text-black">
                                                <strong>{{ project.name }}</strong> {{ project.size }}m² <span class="text-sm text-gray-400 uppercase">{{project.projectUser.areaShort}}</span>
                                            </h4>
                                            <p class="text-sm text-gray-600">{{ project.status.subtitle }}</p>
                                        </div>

                                        <span class="ml-3 text-xl text-green-600 pi pi-check-circle"></span>
                                    </router-link>
                                </div>
                            </div>
                        </tab-panel>

                        <tab-panel>
                            <template slot="header">
                                <h3 class="text-lg">Katkestatud</h3>
                            </template>

                            <div class="py-8">
                              <div class="flex flex-wrap -mx-3">
                                <div class="w-full px-3 md:w-1/2">
                                  <router-link
                                      v-for="project in projects.stopped"
                                      :key="'declined_' + project.id + '_' + project.projectUser.id"
                                      :to="'/project/' + project.projectUser.id"
                                      class="flex items-center justify-between px-4 py-4 mb-4 bg-white rounded-lg shadow-lg md:px-6"
                                  >
                                    <div>
                                      <h4 class="mb-1 text-lg leading-tight text-black">
                                        <strong>{{ project.name }}</strong> {{ project.size }}m² <span class="text-sm text-gray-400 uppercase">{{project.projectUser.areaShort}}</span>
                                      </h4>
                                    </div>

                                    <span v-if="ProjectUserStatus.DECLINED === project.projectUser.status.id"
                                          class="ml-3 text-xl text-red-600 pi pi-times-circle">
                                          </span>
                                    <span v-else-if="ProjectUserStatus.UNRESPONSIVE === project.projectUser.status.id"
                                          class="ml-3 text-xl text-red-600 pi pi-circle">
                                          </span>
                                    <span v-else-if="ProjectUserStatus.NOT_CHOSEN === project.projectUser.status.id"
                                          class="ml-3 text-xl text-red-600 pi pi-stop-circle">
                                        </span>
                                    <span v-else-if="ProjectUserStatus.STOPPED === project.projectUser.status.id"
                                          class="ml-3 text-xl text-red-600 pi pi-stop-circle">
                                        </span>
                                  </router-link>
                                </div>

                                <div class="w-full md:w-1/2">
                                  <template>
                                    <card>
                                      <template #content>
                                        <div class="py-1">
                                          <i class="text-xl text-red-600 pi pi-stop-circle"/> Ei ole valitud projekti
                                        </div>
                                        <div class="py-1">
                                          <i class="text-xl text-red-600 pi pi-times-circle"/> Oled projektist loobunud
                                        </div>
                                        <div class="py-1">
                                          <i class="text-xl text-red-600 pi pi-circle"/> Oled jätnud vastamata
                                        </div>
                                      </template>
                                    </card>
                                  </template>
                                </div>
                              </div>
                            </div>
                        </tab-panel>
                    </tab-view>
        </user-layout>
    </div>
</template>

<script>
import UserLayout from './../Layouts/UserLayout'
import { mapGetters } from 'vuex';
import {ProjectUserRole} from "@/utils/projectUser.role";
import {UserRole} from "@/utils/user.role";
import {ProjectUserStatus} from "@/utils/projectUser.status";
import {ProjectStatus} from "@/utils/project.status";
import {getProjects} from "@/store/project.api";
import {getNotifications} from "@/store/user.api";
import {clearNotification} from "@/store/general.api";

export default {
    components: {
        UserLayout
    },
    computed: {
      ProjectUserStatus() {
        return ProjectUserStatus
      },
      UserRole() {
        return UserRole
      },
      ProjectUserRole() {
        return ProjectUserRole
      },
      ProjectStatus() {
        return ProjectStatus
      },
        ...mapGetters({
            user: 'global/user',
            isAdmin: 'global/isAdmin',
        }),
        chartData() {
            return {
                datasets: [
                    {
                        data: [this.specialistSummary.tasks?.active?.done, this.specialistSummary.tasks?.active?.todo],
                        borderWidth: 0,
                        backgroundColor: [
                            '#009FB7',
                            '#F69272'
                        ],
                        hoverBackgroundColor: [
                            '#009FB7',
                            '#F69272'
                        ]
                    }
                ],
            };
        }
    },
    data() {
        return {
            projects: {
                active: [],
                pending: [],
                ended: [],
                stopped: [],
            },
            toast: null,
            clientSummary: null,
            specialistSummary: {
              active_sum: null,
              pending_sum: null,
              tasks: {
                active: {
                  done: 0,
                  done_sum: 0,
                  todo: 0,
                  todo_sum: 0,
                  total: 0,
                },
                pending: {
                  total: 0,
                },
              },
            },
            chartOptions: {
                tooltips: false,
                responsive: true,
                legend: false,
                aspectRatio: 1,
                cutoutPercentage: 82,
                layout: {
                    padding: 16
                }
            },
            notifications: [],
        }
    },
    methods: {
      closeNotification(url) {
        if (url) {
          clearNotification(url);
        }
      }
    },
    async mounted() {
        const { data } = await getProjects();
        if (data) {
            this.projects.active = data?.projects?.active || [];
            this.projects.pending = data?.projects?.pending || [];
            this.projects.ended = data?.projects?.ended || [];
            this.projects.stopped = data?.projects?.stopped || [];
            if (data?.clientSummary) {
              this.clientSummary = data?.clientSummary;
            }
            if (data?.specialistSummary) {
              this.specialistSummary = data?.specialistSummary;
            }
        }

        getNotifications().then((resp) => {
          this.notifications = resp.data;
        });
    }
}
</script>
